import React, { useEffect, useMemo, useState } from 'react';
import { EventJsonLd } from 'next-seo';
import { hexToRgb, rgbDataURL } from 'corporate-utils';
import { useRouter } from 'next/router';
import { useMediaQuery, useTheme } from '@mui/material';

// Components
import Markdown from '../../components/markdown/Markdown';

// Utils
import { getStrapiMedia } from '../../utils/media';
import { mapToEventJsonLd } from '../../utils/schemas';
import { correctHeightProportion } from '../../utils/image';

// Models
import { Event } from '../../models/auto-generated-strapi-types/types/Event';
import { HeaderCoverResizeType } from '../../models/enum';

// Style
import {
  Category,
  ContainerSingleSymbol,
  ContainerSymbols,
  CoverContainer,
  IconContainer,
  ImageContainerDefaultStyled,
  ImageContainerStyled,
  ImageRowContainer,
  InformationRow,
  InformationRowsContainer,
  InformationsContainer,
  LabelStyled,
  LinkStyled,
  MainInfoContainer,
  MainInfoWrapper,
  MarkdownWrapper,
  OverlayStyled,
  SectionHeaderContainer,
  StyledContentWrapper,
  StyledVideo,
  Title,
} from './Event.style';
import { SingleContentWrapperMobile } from '../../styles/shared';
import { DEFAULT_IMAGE } from '../../models/domain/const';
import ImageWithFallback from '../../components/image-with-fallback/ImageWithFallback';
import { Companies } from '../../models/domain/enum';
import SectionHeaderCms from '../../components/section-header-cms/SectionHeaderCms';
import Divider from '../../components/divider/Divider';
import {
  LocationOnIcon,
  CalendarTodayIcon,
  StorefrontIcon,
  OpenInNewIcon,
} from '../../assets';

const EventPage: React.FC<any> = ({
  data,
  translations,
  isPrerenderRequest,
}: {
  data: any;
  translations: any;
  isPrerenderRequest: boolean;
}) => {
  const { locale } = useRouter();
  const event: Event = data?.events?.data[0] ?? {};
  const { startDate, endDate } = event?.attributes;
  const overlayColor = event?.attributes?.overlayColor;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('sm'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  const image = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? event?.attributes?.coverImage
        : event?.attributes?.mobileCoverImage?.data
          ? event?.attributes?.mobileCoverImage
          : event?.attributes?.coverImage,
    [
      event?.attributes?.coverImage,
      event?.attributes?.mobileCoverImage,
      isDesktop,
    ]
  );

  const video = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? event?.attributes?.video
        : event?.attributes?.mobileVideo?.data
          ? event?.attributes?.mobileVideo
          : event?.attributes?.video,
    [event?.attributes?.mobileVideo, event?.attributes?.video, isDesktop]
  );

  const {
    categoryFontSize,
    categoryMobileFontSize,
    titleFontSize,
    titleMobileFontSize,
    titleFontWeight,
    titleMobileFontWeight,
    categoryFontWeight,
    categoryMobileFontWeight,
    forceDescriptionToBeWhite,
    forceHideLocation,
    forceHideStand,
    forceHideStartDate,
    forceHideEndDate,
    forceHideFair,
    forceHideDate,
    forceHideCategory,
    forceHideTitle,
    forceHideBreadcrumb,
    videoAutoPlay,
    videoControls,
    videoLoop,
    videoMuted,
    website,
  } = event?.attributes || {};

  const atLeastSomethingInDescriptionIsTrue = () => {
    return (
      !forceHideLocation ||
      !forceHideStand ||
      !forceHideStartDate ||
      !forceHideEndDate ||
      !forceHideFair ||
      !forceHideDate ||
      !forceHideCategory ||
      !forceHideTitle ||
      !forceHideBreadcrumb
    );
  };

  const categoryFieldData = !forceHideCategory
    ? [event?.attributes?.category || translations?.urls?.event]
    : [];
  if (!forceHideCategory) {
    event?.attributes?.types?.data
      ?.filter((type) => type?.attributes?.type)
      .map((type) => {
        categoryFieldData.push(type?.attributes?.type);
      });
  }
  if (!forceHideDate) {
    categoryFieldData.push((event?.attributes as any)?.date);
  }

  const { headerCoverResize } = event?.attributes;

  return (
    <StyledContentWrapper>
      {/* Image and description */}
      <CoverContainer headerCoverResize={headerCoverResize}>
        {/* COVER IMAGE */}
        {/* When image is cover */}
        <ImageContainerDefaultStyled headerCoverResize={headerCoverResize}>
          {image &&
            image.data &&
            image.data.attributes &&
            (HeaderCoverResizeType.DEFAULT_COVER ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'cover'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url || '')}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerDefaultStyled>
        {/* COVER IMAGE */}
        {/* When image is contain */}
        <ImageContainerStyled>
          {image &&
            image?.data &&
            image?.data?.attributes &&
            (HeaderCoverResizeType.CONTAIN ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'contain'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  width={'1920'}
                  height={
                    correctHeightProportion(
                      image?.data?.attributes?.height,
                      image?.data?.attributes?.width
                    ) || '600'
                  }
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url || '')}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerStyled>
        {/* Overlay cover */}
        {(typeof window === 'undefined' || isDesktop) && overlayColor ? (
          <OverlayStyled overlay={overlayColor}></OverlayStyled>
        ) : (
          <></>
        )}
      </CoverContainer>

      {/* Description under the image for Sirman */}
      {data?.company === Companies.SIRMAN || !data?.company ? (
        atLeastSomethingInDescriptionIsTrue() ? (
          <MainInfoWrapper lightContrast={false || forceDescriptionToBeWhite}>
            <MainInfoContainer>
              {categoryFieldData && (
                <Category
                  fontSize={categoryFontSize}
                  fontMobileSize={categoryMobileFontSize}
                  fontWeight={categoryFontWeight}
                  fontMobileWeight={categoryMobileFontWeight}
                >
                  {categoryFieldData?.[0]}
                </Category>
              )}
              {!forceHideTitle && (
                <Title
                  fontSize={titleFontSize}
                  fontMobileSize={titleMobileFontSize}
                  fontWeight={titleFontWeight}
                  fontMobileWeight={titleMobileFontWeight}
                >
                  <Markdown>{event?.attributes?.title}</Markdown>
                </Title>
              )}
              {(!forceHideLocation ||
                (!forceHideEndDate && !forceHideEndDate) ||
                !forceHideStand ||
                website) && (
                <InformationsContainer>
                  <Divider />
                  <InformationRowsContainer>
                    {!forceHideLocation && (
                      <InformationRow>
                        <IconContainer>
                          <LocationOnIcon />
                        </IconContainer>
                        <LabelStyled>{event?.attributes?.location}</LabelStyled>
                      </InformationRow>
                    )}
                    {!forceHideEndDate && !forceHideEndDate && (
                      <InformationRow>
                        <IconContainer>
                          <CalendarTodayIcon />
                        </IconContainer>
                        <LabelStyled>
                          {!forceHideStartDate
                            ? startDate + (!forceHideEndDate ? ' - ' : '')
                            : ''}
                          {!forceHideEndDate ? endDate : ''}
                        </LabelStyled>
                      </InformationRow>
                    )}
                    {!forceHideStand && (
                      <InformationRow>
                        <IconContainer>
                          <StorefrontIcon />
                        </IconContainer>
                        <LabelStyled>
                          {
                            translations?.pages?.newsEvents?.singleEvent
                              ?.pavilion
                          }
                          {event?.attributes?.stand}
                        </LabelStyled>
                      </InformationRow>
                    )}
                    {website && (
                      <InformationRow>
                        <IconContainer>
                          <OpenInNewIcon />
                        </IconContainer>
                        <LinkStyled href={website}>{website}</LinkStyled>
                      </InformationRow>
                    )}
                  </InformationRowsContainer>
                  <Divider />
                </InformationsContainer>
              )}
            </MainInfoContainer>
          </MainInfoWrapper>
        ) : (
          <></>
        )
      ) : (
        <></>
      )}

      {/* Description under the image for Felsinea */}
      {data?.sectionHeader && data?.company === Companies.FELSINEA && (
        <SectionHeaderContainer>
          <SectionHeaderCms
            id={data?.id}
            description={data?.sectionHeader?.description}
            personalization={data?.sectionHeader?.personalization}
            mainTitle={data?.sectionHeader?.mainTitle}
            title={data?.sectionHeader?.title}
            backgroundColor={data?.sectionHeader?.backgroundColor}
            topIcon={data?.sectionHeader?.topIcon}
            isPrerenderRequest={isPrerenderRequest}
          />
        </SectionHeaderContainer>
      )}
      {/* Event partecipation */}
      {data?.company === Companies.FELSINEA &&
      (event?.attributes?.labelNation ||
        event?.attributes?.logoNation?.data?.attributes?.url ||
        event?.attributes?.labelParticipation ||
        event?.attributes?.logoPartecipation?.data?.attributes?.url) ? (
          <ContainerSymbols>
            {(event?.attributes?.labelParticipation ||
            event?.attributes?.logoPartecipation?.data?.attributes?.url) && (
              <ContainerSingleSymbol>
                <span>
                  {
                    translations?.pages?.newsEvents?.pastEventsSlider
                      ?.participation
                  }
                </span>
                {event?.attributes?.logoPartecipation?.data?.attributes?.url && (
                  <ImageRowContainer>
                    <ImageWithFallback
                      isPrerenderRequest={isPrerenderRequest}
                      layout="fill"
                      objectFit="contain"
                      src={getStrapiMedia(
                        event?.attributes?.logoPartecipation?.data?.attributes
                          ?.url ?? ''
                      )}
                      alt={
                        event?.attributes?.logoPartecipation?.data?.attributes
                          ?.alternativeText || ''
                      }
                      fallbackSrc={DEFAULT_IMAGE}
                    />
                  </ImageRowContainer>
                )}

                {event?.attributes?.labelParticipation ?? ''}
              </ContainerSingleSymbol>
            )}
            {(event?.attributes?.labelNation ||
            event?.attributes?.logoNation?.data?.attributes?.url) && (
              <ContainerSingleSymbol>
                <span>
                  {translations?.pages?.newsEvents?.pastEventsSlider?.nation}
                </span>
                {event?.attributes?.logoNation?.data?.attributes?.url && (
                  <ImageRowContainer>
                    <ImageWithFallback
                      isPrerenderRequest={isPrerenderRequest}
                      layout="fill"
                      objectFit="contain"
                      src={getStrapiMedia(
                        event?.attributes?.logoNation?.data?.attributes?.url ?? ''
                      )}
                      alt={
                        event?.attributes?.logoNation?.data?.attributes
                          ?.alternativeText || ''
                      }
                      fallbackSrc={DEFAULT_IMAGE}
                    />
                  </ImageRowContainer>
                )}

                {event?.attributes?.labelNation ?? ''}
              </ContainerSingleSymbol>
            )}
          </ContainerSymbols>
        ) : (
          <></>
        )}

      {/* Text from markdown */}
      {event?.attributes?.text && (
        <SingleContentWrapperMobile>
          <MarkdownWrapper>
            <Markdown children={event.attributes.text}></Markdown>
          </MarkdownWrapper>
        </SingleContentWrapperMobile>
      )}

      {/* SEO information */}
      <EventJsonLd {...mapToEventJsonLd(event as any, translations, locale)} />
    </StyledContentWrapper>
  );
};

export default EventPage;
